import React from 'react';
import {getUsersByIds} from '../../../firebase/firestore/users';
import {AI_AUTHOR, AI_AUTHOR_UID, sameAuthorIdList, sortChartAuthorIds} from '../ai/chartAuthorship';

function sameAuthorProfiles(prev, next) {
    if (prev === next) return true;
    if (prev.length !== next.length) return false;
    return prev.every((author, index) => {
        const other = next[index];
        return author.uid === other.uid
            && author.displayName === other.displayName
            && author.profilePicture === other.profilePicture
            && Boolean(author.hasAvatar) === Boolean(other.hasAvatar)
            && Boolean(author.isAI) === Boolean(other.isAI);
    });
}

function profilesForIds(ids, users = []) {
    const byId = new Map(users.map((user) => [user.uid || user.id, user]));
    return ids.map((uid) => {
        if (uid === AI_AUTHOR_UID) return {...AI_AUTHOR};
        const user = byId.get(uid);
        return {
            uid,
            displayName: user?.displayName || user?.name || uid,
            profilePicture: user?.profilePicture || user?.photoURL || null,
            hasAvatar: Boolean(user?.profilePicture || user?.photoURL || user?.hasAvatar),
        };
    });
}

export function useChartAuthorProfiles(authorIds) {
    const [authors, setAuthors] = React.useState([]);
    const idsKey = sortChartAuthorIds(authorIds).join('\0');

    React.useEffect(() => {
        let cancelled = false;
        const ids = idsKey ? idsKey.split('\0') : [];

        const apply = (users = []) => {
            if (cancelled) return;
            setAuthors((prev) => {
                const next = profilesForIds(ids, users);
                return sameAuthorProfiles(prev, next) ? prev : next;
            });
        };

        if (!ids.length) {
            apply([]);
            return undefined;
        }

        const humanIds = ids.filter((id) => id !== AI_AUTHOR_UID);
        if (!humanIds.length) {
            apply([]);
            return undefined;
        }

        getUsersByIds(humanIds).then(apply).catch(() => apply([]));
        return () => {
            cancelled = true;
        };
    }, [idsKey]);

    return authors;
}

export function commitVisibleAuthorIds(prevIds, nextIds) {
    const next = Array.isArray(nextIds) ? nextIds : [];
    return sameAuthorIdList(prevIds, next) ? prevIds : next;
}
